<!--
  @component BindingIO — P4 effecting lens (conformance-gated binding).
  chain:  ctx.deployment.dispatch("binding.*", …) → this → ctx.binding
  effects: reverify a substrate seam's bound driver against the
           conformance contract (a control-plane gate that re-runs
           the binding's verdict on demand).
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { BINDING_IO_KEY } from "./deployment-io.js";
  import type { BindingIOContext } from "./deployment-io.js";
  export { BINDING_IO_KEY };
  export type { BindingIOContext };
  export function useBindingIO(): BindingIOContext {
    const ctx = getContext<BindingIOContext | undefined>(BINDING_IO_KEY);
    if (!ctx) throw new Error("useBindingIO() called outside of a BindingIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: BindingIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("binding.")); },
    reverify: (seam) => dispatchOp("binding.reverify", { seam }),
  } satisfies BindingIOContext;

  setContext(BINDING_IO_KEY, ctx);
</script>
{@render children?.()}
